import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import MenuDropdown from "@/components/MenuDropdown";
import TextField from "@/components/TextField";
import Banner from "@/components/Banner";
import { GET_NON_ACTIVE_CLASSIFICATIONS_QUERY, GET_SECTIONS_QUERY } from "../queries";
import { UPDATE_MEMBERSHIP_MUTATION } from "../mutations";
import { Person } from "@/types";
import { ApolloError } from "@apollo/client";
import { GET_MEMBERSHIP_DETAILS_QUERY } from "../../person/queries";
import SingleDatePicker from "../../person/_components/SingleDatePicker";
import { showSnackbar } from "@/lib/snackbarUtils";

interface UpdateMembershipModalProps {
  open: boolean;
  onClose: () => void;
  person: Person;
  pgaId: string;
}

type FormValues = {
  classificationId: string;
  sectionId: string;
  startDate: string;
  note: string;
};

const NON_ACTIVE_CLASSIFICATIONS = ["HM", "LMA", "LMMA", "LMC", "LM", "LMM", "Deprecated", "RSV", "RM", "F"];

const getClassificationBannerMessage = (classificationName?: string): string | null => {
  if (!classificationName) return null;

  const bannerMessages: Record<string, string> = {
    HM: "Honorary Members are elected by the Delegates to the Annual Meeting for their outstanding contributions to the game of golf.",
    LM: "Life Member (Retired) are Members who have held a minimum of 20 years in an Active Classification (whether continuous or not) and who do not otherwise, based on current employment status, qualify for an Active Classification.",
    LMA: "Life Member (Active) are Members who have held a minimum of 20 years in an Active Classification (whether continuous or not) and who do not otherwise, based on current employment status, qualify for an Active Classification.",
    LMC: "Life Member - Century are Life Members who are fully retired, are no longer active in the Association in any manner and have achieved a minimum age of 75 years and a minimum of 25 years of Active classifications. This is a one-time only permanent transfer into Life Member Century.",
    LMM: "Life Member Master (Retired) are Members who have achieved the Master Professional designation, have held a minimum of 20 years in an Active Classification (whether continuous or not), and are fully retired from active employment in the golf industry.",
    LMMA: "Life Member Master - Active are Members who have achieved the Master Professional designation, have held a minimum of 20 years in an Active Classification (whether continuous or not), and are primarily employed in an area required by an Active Classification.",
    RSV: "Reserve Members are Members who are not eligible for classification as Active or Life Members.",
    RM: "Retired Members are Members who are retired and have achieved a total of 65 years or more for age and years of Active classifications and who are not eligible for another classification.",
    F: "Class “F” Members are Members who fail to meet the requirements of the Professional Development (PDR) Program. Members who do not satisfy these requirements by the end of the Professional Development Program period in which they were transferred to Class F shall be terminated from membership."
  };

  return bannerMessages[classificationName] || null;
};

export default function UpdateMembershipModal({ open, onClose, person, pgaId }: UpdateMembershipModalProps) {
  const [bannerError, setBannerError] = useState("");

  const { data: classificationsData, loading: classificationsLoading } = useQuery(GET_NON_ACTIVE_CLASSIFICATIONS_QUERY);
  const { data: sectionsData, loading: sectionsLoading } = useQuery(GET_SECTIONS_QUERY, {
    variables: { input: {} }
  });

  const [updateMembership, { loading: isSubmitting }] = useMutation(UPDATE_MEMBERSHIP_MUTATION, {
    refetchQueries: [
      {
        query: GET_MEMBERSHIP_DETAILS_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid }
  } = useForm<FormValues>({
    defaultValues: {
      classificationId: "",
      sectionId: "",
      startDate: "",
      note: ""
    },
    mode: "onChange"
  });

  const selectedClassificationId = watch("classificationId");
  const defaultSectionId = person.sectionByHomeAddress?.id;

  useEffect(() => {
    if (open) {
      reset({
        classificationId: "",
        sectionId: defaultSectionId || "",
        startDate: "",
        note: ""
      });
      setBannerError("");
    }
  }, [open, reset, defaultSectionId]);

  const selectedClassification = useMemo(() => {
    if (!selectedClassificationId || !classificationsData?.classifications?.nodes) return null;
    return classificationsData.classifications.nodes.find(
      (c: { id: string; name: string }) => c.id === selectedClassificationId
    );
  }, [selectedClassificationId, classificationsData]);

  const classificationBannerMessage = useMemo(() => {
    return selectedClassification ? getClassificationBannerMessage(selectedClassification.name) : null;
  }, [selectedClassification]);

  const classificationOptions = useMemo(() => {
    if (!classificationsData?.classifications?.nodes) return [];

    return classificationsData.classifications.nodes
      .filter((c: { id: string; name: string }) => NON_ACTIVE_CLASSIFICATIONS.includes(c.name))
      .map((c: { id: string; name: string; description: string }) => ({
        value: c.id,
        label: `${c.description} (${c.name})`,
        displayText: c.name
      }));
  }, [classificationsData]);

  const sectionOptions =
    sectionsData?.crmSections?.nodes.map((s: { id: string; name: string }) => ({
      value: s.id,
      label: s.name,
      displayText: s.name
    })) || [];

  const onSubmit = async (formData: FormValues) => {
    try {
      setBannerError("");

      if (!formData.startDate) {
        setBannerError("Start date is required");
        return;
      }

      if (!formData.classificationId) {
        setBannerError("Classification is required");
        return;
      }

      if (!formData.sectionId) {
        setBannerError("Section is required");
        return;
      }

      const { data } = await updateMembership({
        variables: {
          input: {
            personId: person.id,
            classificationId: formData.classificationId,
            sectionId: formData.sectionId,
            startedOn: formData.startDate,
            note: formData.note || null
          }
        }
      });

      const result = data?.updateMembership || { success: false, message: "Failed to update membership" };

      if (result.success) {
        showSnackbar(result.message || "Membership updated");
        onClose();
      } else {
        throw new Error(result.message || "Failed to update membership");
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Update Membership"
      className="w-[672px max-h-[85vh]"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="update-membership-cancel-button" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            testId="update-membership-save-button"
            disabled={isSubmitting || !isValid}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <Controller name="sectionId" control={control} render={({ field }) => <input type="hidden" {...field} />} />

        {!!bannerError && <Banner inline variant="error" description="The update failed." subText={bannerError} />}

        <Controller
          name="classificationId"
          control={control}
          rules={{ required: "Non-Active Classification is required" }}
          render={({ field }) => {
            const selectedClassification = classificationOptions.find(
              (c: { value: string }) => c.value === field.value
            );
            return (
              <div>
                <MenuDropdown<{ value: string; label: string; displayText: string }>
                  label="Non-Active Classification *"
                  options={classificationOptions}
                  value={selectedClassification}
                  onSelect={(option) => field.onChange(option.value)}
                  getLabel={(option) => option.label}
                  renderItem={(option) => <div className="text-body-large">{option.label}</div>}
                  disabled={classificationsLoading}
                />
                {errors.classificationId && (
                  <p className="mt-1 ml-3 text-body-small text-sys-brand-error" role="alert">
                    {errors.classificationId.message}
                  </p>
                )}
              </div>
            );
          }}
        />

        {classificationBannerMessage && (
          <div className="[&_h3]:text-body-medium [&_h3]:font-normal">
            <Banner inline variant="info" description={classificationBannerMessage} />
          </div>
        )}

        <Controller
          name="sectionId"
          control={control}
          rules={{ required: "Section is required" }}
          render={({ field }) => {
            const selectedSection = sectionOptions.find((s: { value: string }) => s.value === field.value);
            return (
              <div>
                <MenuDropdown<{ value: string; label: string; displayText: string }>
                  label="Section *"
                  options={sectionOptions}
                  value={selectedSection}
                  onSelect={(option) => field.onChange(option.value)}
                  getLabel={(option) => option.label}
                  renderItem={(option) => <div className="text-body-large">{option.label}</div>}
                  disabled={sectionsLoading}
                />
                {errors.sectionId && (
                  <p className="mt-1 ml-3 text-body-small text-sys-brand-error" role="alert">
                    {errors.sectionId.message}
                  </p>
                )}
                {!errors.sectionId && (
                  <p className="mt-1 ml-3 text-body-small text-sys-brand-on-surface-variant">
                    Default section for non-active classifications is home address
                  </p>
                )}
              </div>
            );
          }}
        />

        <SingleDatePicker
          control={control}
          label="Start Date"
          name="startDate"
          required
          useModal
          supportingText="The end date of the current membership will be set to the day prior to this start date"
        />

        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextField label="Note" type="textarea" rows={3} value={field.value} onChangeText={field.onChange} />
          )}
        />
      </div>
    </Dialog>
  );
}
